
import React, { useState, useRef, useEffect } from 'react';
import { 
  Image as ImageIcon, 
  X, 
  Sparkles, 
  ArrowUp, 
  Globe, 
  Volume2
} from 'lucide-react';
import { Message, AppLanguage, ChatbotPersonality, UserProfile, ViewType } from '../types';
import { chatWithGeminiStream, generateSpeech } from '../services/gemini';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Prism from 'prismjs';

interface ChatViewProps {
  onUseCredit: () => boolean;
  isPro: boolean;
  language: AppLanguage;
  personality: ChatbotPersonality;
  user: UserProfile;
  onNavigate: (view: ViewType) => void;
}

const ChatView: React.FC<ChatViewProps> = ({ onUseCredit, isPro, language, personality, user, onNavigate }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    Prism.highlightAll();
  }, [messages, streamingText, isLoading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  }, [input]);

  const getSystemInstruction = () => {
    return `You are Zomi Generative AI, an elite AI assistant for the Zomi community. You operate in ${isPro ? 'Pro Reasoning Mode' : 'Standard Flash Mode'}. 
    Respond primarily in ${language === 'zo' ? 'Zomi' : 'clear English'}. Maintain deep cultural context, be precise, helpful, and maintain a high standard of intelligence. Use proper Title Case where appropriate.`;
  };

  const handleSend = async (overrideInput?: string) => {
    const textToSend = overrideInput || input.trim();
    if ((!textToSend && !selectedImage) || isLoading) return;
    if (!onUseCredit()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      image: selectedImage || undefined,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSelectedImage(null);
    setIsLoading(true);
    setStreamingText("");

    try {
      const responseData = await chatWithGeminiStream(
        textToSend || "Describe this image.", 
        userMsg.image, 
        isPro, 
        getSystemInstruction(),
        (text) => setStreamingText(text)
      );
      
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: responseData.text,
        sources: responseData.sources,
        timestamp: Date.now()
      }]);
    } catch (error) {
      console.error("Chat Error:", error);
    } finally {
      setIsLoading(false);
      setStreamingText("");
    }
  };

  const handleSpeak = async (text: string) => {
    try {
      const audioData = await generateSpeech(text);
      if (audioData) {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        }
        const ctx = audioContextRef.current;
        const binaryString = atob(audioData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const dataInt16 = new Int16Array(bytes.buffer);
        const buffer = ctx.createBuffer(1, dataInt16.length, 24000);
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < dataInt16.length; i++) channelData[i] = dataInt16[i] / 32768.0;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
      }
    } catch (e) {
      console.error("Audio error", e);
    }
  };

  const suggestionCards = [
    { label: 'Zomi History', sub: 'CULTURAL ANALYSIS' },
    { label: 'Translate Poetry', sub: 'LINGUISTIC BRIDGE' },
    { label: 'Image Creation', sub: 'AI ART STUDIO' },
    { label: 'Web Research', sub: 'LIVE SEARCH' }
  ];

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] dark:bg-[#0d0d0d] relative transition-colors duration-300 overflow-hidden font-sans">
      <div className="flex-1 overflow-y-auto pt-6 pb-40 md:pb-48 no-scrollbar">
        {messages.length === 0 ? (
          <div className="max-w-2xl mx-auto px-6 h-full flex flex-col items-center justify-center animate-fade-in py-10">
            <div className="w-full space-y-4 mb-10">
              {suggestionCards.map((item, i) => (
                <button 
                  key={i}
                  onClick={() => handleSend(item.label)}
                  className="w-full p-8 rounded-[2rem] bg-white dark:bg-[#1a1a1b] border border-slate-200/60 dark:border-white/5 hover:border-blue-500/30 dark:hover:border-blue-500/30 transition-all text-left group active:scale-[0.99] shadow-[0_2px_15px_-3px_rgba(0,0,0,0.03)] dark:shadow-none"
                >
                  <p className="text-[20px] font-bold text-slate-900 dark:text-white mb-2 tracking-tight">{item.label}</p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 font-bold tracking-[0.15em] uppercase">{item.sub}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-5 md:px-10 space-y-12">
            {messages.map((msg) => (
              <div key={msg.id} className="animate-fade-in">
                <div className="flex gap-4 items-start">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-1 shadow-sm ${
                    msg.role === 'assistant' ? 'bg-blue-600/5 dark:bg-white/5' : 'bg-blue-600 shadow-lg shadow-blue-500/10'
                  }`}>
                    {msg.role === 'assistant' ? <Sparkles size={18} className="text-blue-500" /> : <span className="text-[12px] font-bold text-white uppercase">{user.username?.charAt(0) || 'U'}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-black text-slate-400 dark:text-slate-600 tracking-widest uppercase">
                        {msg.role === 'assistant' ? (isPro ? 'Pro Intelligence' : 'ZomiGenAI') : 'You'}
                      </div>
                      {msg.role === 'assistant' && (
                        <button 
                          onClick={() => handleSpeak(msg.content)}
                          className="p-1.5 text-slate-300 dark:text-slate-700 hover:text-blue-500 transition-colors"
                        >
                          <Volume2 size={14} />
                        </button>
                      )}
                    </div>
                    {msg.image && (
                      <div className="mb-4 inline-block">
                        <img src={`data:image/png;base64,${msg.image}`} className="max-w-full sm:max-w-xs rounded-2xl border border-slate-200 dark:border-white/5 shadow-md" alt="Uploaded" />
                      </div>
                    )}
                    <div className="text-slate-800 dark:text-slate-200 leading-relaxed text-[16px] markdown-content tracking-normal">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                    {msg.sources && msg.sources.length > 0 && (
                      <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-slate-100 dark:border-white/5">
                        {msg.sources.map((s, idx) => (
                          <a key={idx} href={s.uri} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-[#1a1a1b] border border-slate-200 dark:border-white/5 rounded-lg text-[11px] font-bold text-slate-500 hover:text-blue-600 transition-all shadow-sm">
                            <Globe size={12} /> {s.title}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {streamingText && (
               <div className="flex gap-4 items-start animate-fade-in">
                  <div className="w-9 h-9 rounded-full bg-blue-600/5 dark:bg-white/5 flex items-center justify-center shrink-0 mt-1">
                    <Sparkles size={18} className="text-blue-500 animate-pulse" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-black text-blue-500/40 tracking-widest mb-2 uppercase">Processing...</div>
                    <div className="text-slate-800 dark:text-slate-200 leading-relaxed text-[16px] markdown-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
                    </div>
                  </div>
               </div>
            )}
            <div ref={scrollRef} className="h-12" />
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc] dark:from-[#0d0d0d] dark:via-[#0d0d0d] to-transparent pt-10 pb-6 px-5 z-20">
        <div className="max-w-2xl mx-auto flex flex-col items-center">
          {selectedImage && (
            <div className="w-full flex justify-start mb-3">
               <div className="relative">
                 <img src={`data:image/png;base64,${selectedImage}`} className="w-14 h-14 object-cover rounded-xl border border-slate-200 dark:border-white/10 shadow-lg ring-2 ring-white dark:ring-[#0d0d0d]" alt="Selected" />
                 <button onClick={() => setSelectedImage(null)} className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white shadow-md active:scale-90 transition-transform">
                    <X size={12} strokeWidth={3} />
                 </button>
               </div>
            </div>
          )}

          <div className="w-full relative flex items-center bg-white dark:bg-[#1a1a1b] border border-slate-200 dark:border-white/5 rounded-[2.5rem] p-1.5 shadow-[0_2px_20px_-5px_rgba(0,0,0,0.05)] focus-within:ring-4 focus-within:ring-blue-500/5 transition-all">
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="p-3.5 text-slate-400 dark:text-slate-600 hover:text-blue-500 transition-colors active:scale-90"
            >
              <ImageIcon size={22} />
            </button>
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const r = new FileReader();
                r.onloadend = () => setSelectedImage((r.result as string).split(',')[1]);
                r.readAsDataURL(file);
              }
            }} />
            
              <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
              placeholder={'Ask ZomiGenAI...'}
              className="flex-1 bg-transparent text-slate-900 dark:text-white py-3 px-2 focus:outline-none resize-none text-[16px] placeholder:text-slate-400 dark:placeholder:text-slate-700 max-h-32 no-scrollbar font-medium"
            />
            
            <div className="h-6 w-[1px] bg-slate-200 dark:bg-white/10 mx-1"></div>

            <button
              onClick={() => handleSend()}
              disabled={(!input.trim() && !selectedImage) || isLoading}
              className={`p-3 rounded-full transition-all ${
                input.trim() || selectedImage ? 'text-blue-600 active:scale-90' : 'text-slate-300 dark:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${input.trim() || selectedImage ? 'border border-blue-600' : 'border border-transparent'}`}>
                <ArrowUp size={20} />
              </div>
            </button>
          </div>
          <p className="mt-5 text-[10px] text-center text-slate-400 dark:text-slate-700 uppercase tracking-[0.4em] font-bold pointer-events-none select-none">
            INTELLIGENCE ARCHITECTURE • 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatView;
